import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import type { ThinkingStep } from '@Models';
import { AGENT_DURATIONS, AGENT_EASING } from '../../../Helpers/Agents/animations';
import ThinkingPhaseList from './ThinkingPhaseList';

interface ThinkingSummaryProps {
  steps: ThinkingStep[];
  seconds?: number | null;
}

/** Collapsed "Thought for Ns · N steps" header above an answer; expands to the step list. */
const ThinkingSummary = ({ steps, seconds = null }: ThinkingSummaryProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  if (steps.length === 0) return null;

  const label =
    seconds !== null && seconds > 0
      ? t('Agent.ThinkingSummary', { seconds, count: steps.length })
      : t('Agent.ThinkingStepsCount', { count: steps.length });

  return (
    <div className="mb-2">
      <button
        type="button"
        aria-expanded={expanded}
        aria-label={expanded ? t('Agent.AriaHideThinkingLog') : t('Agent.AriaShowThinkingLog')}
        onClick={() => setExpanded((v) => !v)}
        className="m-0 inline-flex cursor-pointer items-center gap-1 rounded-md border-0 bg-transparent py-0.5 pl-0 pr-1 font-[inherit] text-[13px] text-gray-500 transition-colors hover:text-gray-800 focus:outline-none focus-visible:ring-1 focus-visible:ring-gray-300">
        <span>{label}</span>
        {expanded ? <ChevronDown size={14} strokeWidth={1.75} /> : <ChevronRight size={14} strokeWidth={1.75} />}
      </button>

      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            key="log"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: AGENT_DURATIONS.base, ease: AGENT_EASING.outExpo }}
            className="mt-1 overflow-hidden rounded-xl border border-[#10182812] bg-[#fbfaf9]">
            <ThinkingPhaseList phases={steps} isLive={false} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ThinkingSummary;
